import { EllipsisHorizontalIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger
} from "@moe/oss/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@moe/oss/ui/dropdown-menu";
import { Chip } from "@moe/priv/components/ui/chip";
import { Character } from "@moe/priv/model/character";
import { cn } from "@moe/priv/utils";
import { useState } from "react";

export interface Action {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onSelect: (e: Event) => void;
  className?: string;
}

interface ThinCharacterCardProps {
  char: Character;
  actions?: Action[];
  showContextMenu?: boolean;
  showDropdownMenu?: boolean;
  openDropdownOnClick?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Renders a horizontal character card with the character's name, image, and optional actions.
 *
 * @param char - The character object to display in the card.
 * @param actions - An optional array of actions to display in the dropdown or context menu.
 * @param showContextMenu - Whether to display a context menu when the card is right-clicked.
 * @param showDropdownMenu - Whether to display a dropdown menu with additional actions.
 * @param openDropdownOnClick - Whether to open the dropdown menu when the card body is clicked.
 * @param onClick - An optional callback function to be called when the card is clicked.
 */
export function ThinCharacterCard({
  char,
  actions = [],
  showContextMenu = true,
  showDropdownMenu = true,
  openDropdownOnClick = false,
  onClick,
  className
}: ThinCharacterCardProps) {
  /**
   * Hacky way to work around radix ui dropdown menu bug
   * @see https://github.com/radix-ui/primitives/issues/2702
   */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const CardContent = (
    <button
      onClick={() => {
        if (openDropdownOnClick) setIsDropdownOpen(true);
        onClick?.();
      }}
      className={cn(
        "text-tx-primary active:bg-accent group flex h-fit w-full select-none items-center justify-between overflow-x-hidden rounded-lg p-3 font-[480] transition duration-200 ease-out hover:brightness-90 focus:outline-none",
        className
      )}
    >
      <div className="mr-3 flex items-center space-x-5">
        <Avatar src={char.avatar} className="shadow-md" />
        <CharacterInfo char={char} />
      </div>
      {showDropdownMenu && actions.length > 0 && (
        <div>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen} modal={false}>
            <DropdownMenuTrigger
              asChild
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <button className="p-2.5">
                <EllipsisHorizontalIcon className="text-tx-secondary size-5 shrink-0 cursor-pointer transition duration-75 ease-out" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-36">
              <DropdownMenuGroup>
                <MenuActions actions={actions} MenuItemComponent={DropdownMenuItem} />
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </button>
  );

  if (!showContextMenu) {
    return CardContent;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger>{CardContent}</ContextMenuTrigger>
      <ContextMenuContent className="w-36">
        <MenuActions actions={actions} MenuItemComponent={ContextMenuItem} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

function MenuActions({ actions, MenuItemComponent }: { actions: Action[]; MenuItemComponent: any }) {
  return (
    <>
      {actions.map(({ label, icon: Icon, onSelect, className }) => (
        <MenuItemComponent
          key={label}
          onSelect={onSelect}
          className={className}
          onClick={(e: any) => e.stopPropagation()}
        >
          {label}
          <ContextMenuShortcut>
            <Icon className="size-4" />
          </ContextMenuShortcut>
        </MenuItemComponent>
      ))}
    </>
  );
}

function CharacterInfo({ char }: { char: Character }) {
  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center gap-3">
        <h3 className="text-tx-secondary line-clamp-1 text-ellipsis text-left text-base font-semibold">{char.title}</h3>
        {char.isPrivate && <Chip>Private</Chip>}
      </div>
      {char.tagline && (
        <p className="text-tx-tertiary line-clamp-1 text-ellipsis text-left text-sm font-semibold">{char.tagline}</p>
      )}
    </div>
  );
}
